import React, { useState } from 'react';
import { X, Percent, Tag, Calendar, DollarSign, AlertCircle } from 'lucide-react';
import { Product } from '../types';

interface AdminPromoModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedProducts: Product[];
  onApplyPromo: (promoData: any) => Promise<void>;
}

const AdminPromoModal: React.FC<AdminPromoModalProps> = ({
  isOpen,
  onClose,
  selectedProducts,
  onApplyPromo
}) => {
  const [promoType, setPromoType] = useState<'percentage' | 'fixed'>('percentage');
  const [promoValue, setPromoValue] = useState<number>(0);
  const [promoLabel, setPromoLabel] = useState('SALE');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const promoData = {
        product_ids: selectedProducts.map(p => p.id),
        promo_label: promoLabel,
        promo_start_date: startDate || null,
        promo_end_date: endDate || null,
        ...(promoType === 'percentage' 
          ? { promo_percentage: promoValue } 
          : { promo_price: promoValue })
      };

      await onApplyPromo(promoData);
      onClose();
    } catch (error) {
      console.error('Error applying promo:', error);
      alert('Failed to apply promo');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center">
      <div className="absolute inset-0 bg-stone-900/60" onClick={onClose}></div>
      
      <div className="relative bg-white w-full max-w-lg rounded-sm shadow-2xl animate-fade-in">
        <div className="px-6 py-4 border-b border-stone-100 flex justify-between items-center bg-stone-50">
          <h3 className="font-serif text-xl text-stone-900 flex items-center gap-2">
            <Tag size={18} className="text-gold-600" />
            Apply Promo to {selectedProducts.length} Product{selectedProducts.length !== 1 ? 's' : ''}
          </h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-900">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Selected Products Preview */}
          <div className="bg-stone-50 p-3 rounded-sm max-h-32 overflow-y-auto">
            <p className="text-xs font-medium text-stone-600 mb-2">Selected Products:</p>
            <div className="space-y-1">
              {selectedProducts.slice(0, 5).map(p => (
                <div key={p.id} className="text-xs text-stone-500 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gold-600 rounded-full"></span>
                  <span>{p.name} - ₱{p.price.toLocaleString()}</span>
                </div>
              ))}
              {selectedProducts.length > 5 && (
                <p className="text-xs text-stone-400">...and {selectedProducts.length - 5} more</p>
              )}
            </div>
          </div>

          {/* Promo Label */}
          <div>
            <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">
              Promo Label
            </label>
            <input
              type="text"
              value={promoLabel}
              onChange={(e) => setPromoLabel(e.target.value)}
              className="w-full border border-stone-300 p-3 rounded-sm focus:border-gold-500 outline-none"
              placeholder="e.g. SALE, SUMMER SALE, FLASH SALE"
              required
            />
          </div>

          {/* Promo Type Selection */}
          <div>
            <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-3">
              Promo Type
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={promoType === 'percentage'}
                  onChange={() => setPromoType('percentage')}
                  className="w-4 h-4 accent-gold-600"
                />
                <Percent size={16} className="text-stone-400" />
                <span className="text-sm">Percentage Off</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={promoType === 'fixed'}
                  onChange={() => setPromoType('fixed')}
                  className="w-4 h-4 accent-gold-600"
                />
                <DollarSign size={16} className="text-stone-400" />
                <span className="text-sm">Fixed Price</span>
              </label>
            </div>
          </div>

          {/* Promo Value */}
          <div>
            <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">
              {promoType === 'percentage' ? 'Discount Percentage' : 'Promo Price (₱)'}
            </label>
            <input
              type="number"
              value={promoValue}
              onChange={(e) => setPromoValue(parseFloat(e.target.value) || 0)}
              className="w-full border border-stone-300 p-3 rounded-sm focus:border-gold-500 outline-none"
              min="0"
              max={promoType === 'percentage' ? 100 : undefined}
              step={promoType === 'percentage' ? 1 : 0.01}
              required
            />
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">
                Start Date
              </label>
              <div className="relative">
                <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full pl-10 pr-3 py-3 border border-stone-300 rounded-sm focus:border-gold-500 outline-none"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">
                End Date
              </label>
              <div className="relative">
                <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full pl-10 pr-3 py-3 border border-stone-300 rounded-sm focus:border-gold-500 outline-none"
                />
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 p-3 rounded-sm flex items-start gap-2">
            <AlertCircle size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700">
              Promo will be applied to all selected products. If both price and end date are set, 
              the promo will automatically expire on the end date.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-stone-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-stone-200 text-stone-600 text-xs uppercase tracking-widest hover:bg-stone-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || selectedProducts.length === 0}
              className="px-6 py-2 bg-gold-600 text-white text-xs uppercase tracking-widest hover:bg-gold-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Applying...
                </>
              ) : (
                <>
                  <Tag size={14} /> Apply Promo
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminPromoModal;